import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthenticatedUser } from '@hygge/shared';
import { announceIdentityChange, api } from '@/lib/api';

interface AuthState {
  user: AuthenticatedUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  isImpersonating: boolean;

  // Actions
  setUser: (user: AuthenticatedUser | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  initialize: () => Promise<void>;
  startImpersonation: (employeeId: string) => Promise<void>;
  exitImpersonation: () => Promise<void>;
}

// Promise deduplication to prevent multiple simultaneous API calls
let initializePromise: Promise<void> | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      isInitialized: false,
      isImpersonating: false,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
          isLoading: false,
          isInitialized: true,
        }),

      setLoading: (isLoading) => set({ isLoading }),

      logout: async () => {
        try {
          // Call backend to clear cookies
          await api.post('/auth/logout');
        } catch {
          // Ignore errors, still clear local state
        }

        // Reset all auth state including isInitialized
        // This ensures next page load will re-check auth
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          isInitialized: true, // Keep initialized to prevent re-check loop
          isImpersonating: false,
        });
      },

      checkAuth: async () => {
        try {
          // Cookies are sent automatically with withCredentials: true
          const response = await api.get('/auth/me');
          set({
            user: response.data,
            isAuthenticated: true,
            isLoading: false,
            isImpersonating: !!response.data.isImpersonated,
          });
          return true;
        } catch {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
          return false;
        }
      },

      initialize: async () => {
        // Already initialized, skip
        if (get().isInitialized) return;

        // If initialization is in progress, wait for it
        if (initializePromise) {
          await initializePromise;
          return;
        }

        // Start initialization
        set({ isLoading: true });

        initializePromise = get().checkAuth().then(() => {
          set({ isInitialized: true });
          initializePromise = null;
        });

        await initializePromise;
      },

      startImpersonation: async (employeeId: string) => {
        const response = await api.post('/auth/impersonate', { employeeId });
        announceIdentityChange();
        set({
          user: response.data,
          isAuthenticated: true,
          isImpersonating: true,
        });
      },

      exitImpersonation: async () => {
        const response = await api.post('/auth/exit-impersonation');
        announceIdentityChange();
        set({
          user: response.data,
          isAuthenticated: true,
          isImpersonating: false,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        // Only persist user data, not auth state
        // Auth state is verified via cookies on each page load
        user: state.user,
      }),
    }
  )
);
